import { Toolbar } from 'primereact/toolbar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tag } from 'primereact/tag';
import { PracticeProblems } from '../PracticeProblems/PracticeProblems';
import { FC, useState } from 'react';
import { PreloadedQuery, useLazyLoadQuery, usePreloadedQuery } from 'react-relay';
import { getLessonsByType, getPresignedURLsForSkill, getSkillInfoByCode } from '../../graphQLOperations/Skills';
import { SkillsTeacherSingleSkillByCodeQuery } from '../../graphQLOperations/__generated__/SkillsTeacherSingleSkillByCodeQuery.graphql';
import { splitSkillContext } from './splitSkillContext';
import DOMPurify from 'dompurify';
import { Dialog } from 'primereact/dialog';
import { WorkedExample } from '../WorkedExample/WorkedExample';
import { downloadPdf } from '../../businessLogicService/downloadPdf';
import { SkillsLessonByTypeQuery } from '../../graphQLOperations/__generated__/SkillsLessonByTypeQuery.graphql';
import { Divider } from 'primereact/divider';
import { getLessonsInfo } from '../../businessLogicService/getLessonsInfo';
import { AlignedContentModal } from '../AlignedContentModal/AlignedContentModal';
import { SelectedLesson } from '../../models/OtherResources';
import { SkillsPresignedURLsQuery } from '../../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';
import { AssignmentSidebarOpenModal } from '../AssignmentSidebar/AssignemntSidebarOpenModal/AssignmentSidebarOpenModal';
import { AssignmentEntryPoint, SelectAssignmentModeModal } from '../../models/Assignment';
import { AssignmentOpenModalButton } from '../Assignments/AssignmentOpenModalButton';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { disableKhanForMiami } from '../../businessLogicService/customBrandHelper';
import { Skill } from '../../models/Skill';
import { TeacherLedResourcesCard } from '../TeacherLedLesson/TeacherLedResourcesCard';
import { pickTeacherLedFiles } from '../../businessLogicService/pickTeacherLedFiles';

interface TeacherSingleSkillProps {
  skillCode?: string;
  skill?: Skill;
  skillsPresignedURLsQueryReference: PreloadedQuery<SkillsPresignedURLsQuery, Record<string, unknown>>;
}

/**
 * The modal version is opened from several screens, so the modal itself is the entry point and the
 * parent must not name a screen. The full-page version has no modal to report, so it must name one.
 */
type Props = TeacherSingleSkillProps &
  (
    | { closeModalVersion: () => void; entryPoint?: never }
    | { closeModalVersion?: never; entryPoint: AssignmentEntryPoint }
  );

export const TeacherSingleSkill: FC<Props> = ({
  skillCode,
  skill,
  entryPoint,
  skillsPresignedURLsQueryReference,
  closeModalVersion,
}) => {
  // Only the full-page version names a screen; the Props union guarantees the modal version does not.
  const assignmentEntryPoint: AssignmentEntryPoint = entryPoint ?? 'assign_from_modal';
  const userRedux = useAppSelector(selectUser);
  const [selectedLesson, setSelectedLesson] = useState<SelectedLesson | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [printDisabled, setPrintDisabled] = useState(true);
  const [showCreateAssignmentModel, setCreateAssignmentModal] = useState<SelectAssignmentModeModal>({
    visible: false,
    skill: null,
  });

  const [fetchingPdf, setFetchingPdf] = useState(false);
  const { siteSpecificSkillInfoBySkillCode } = useLazyLoadQuery<SkillsTeacherSingleSkillByCodeQuery>(
    getSkillInfoByCode,
    {
      skillCode: skillCode || '',
      skip: false,
    },
  );
  const lessonSkillsQuery = useLazyLoadQuery<SkillsLessonByTypeQuery>(getLessonsByType, {
    filter: {
      skillId: siteSpecificSkillInfoBySkillCode?.id,
      isActive: true,
    },
  });

  const teacherLedFiles = pickTeacherLedFiles(lessonSkillsQuery);

  const tag = () => {
    return <Tag severity="warning" value="Resource Available in Student Portal"></Tag>;
  };

  const vocabHeader = () => {
    return (
      <div className="flex gap-md">
        <h3>Vocabulary</h3>
        {tag()}
      </div>
    );
  };
  const whenWillIUseThisHeader = () => {
    return (
      <div className="flex gap-md">
        <h3>When will I use this?</h3>
        {tag()}
      </div>
    );
  };

  const { presignedURLsForSkill } = usePreloadedQuery<SkillsPresignedURLsQuery>(
    getPresignedURLsForSkill,
    skillsPresignedURLsQueryReference,
  );

  return (
    <div className="w-full">
      <AssignmentSidebarOpenModal
        callback={closeModalVersion}
        showCreateAssignmentModel={showCreateAssignmentModel}
        setCreateAssignmentModal={setCreateAssignmentModal}
      />

      <TabView className="col-12 nc_tabView">
        <TabPanel className="gap-lg" header="About this Skill">
          <Toolbar
            end={
              <div style={{ height: '3rem' }}>
                <Button
                  label="Print Skill Information"
                  icon="nc_icon-nc_print icon-sm"
                  outlined
                  rounded
                  aria-label="Print skill information"
                  onClick={() => window.print()}
                ></Button>
                <AssignmentOpenModalButton
                  skill={{
                    id: siteSpecificSkillInfoBySkillCode?.id || '',
                    name: siteSpecificSkillInfoBySkillCode?.name || '',
                    code: siteSpecificSkillInfoBySkillCode?.code || '',
                    targetSkillType: skill?.targetSkillType ?? TargetSkillTypeLabel.TARGET,
                  }}
                  setCreateAssignmentModal={setCreateAssignmentModal}
                  entryPoint={assignmentEntryPoint}
                />
              </div>
            }
            className="w-full nc_block-header tw-h-auto"
          />
          <div className="flex flex-wrap justify-content-between gap-lg">
            <Fieldset legend="Description" className="lg:w-5 w-full">
              {siteSpecificSkillInfoBySkillCode?.summary}
            </Fieldset>
            {teacherLedFiles && (
              <TeacherLedResourcesCard
                skillCode={skillCode || ''}
                skillName={siteSpecificSkillInfoBySkillCode?.name ?? undefined}
                files={teacherLedFiles}
                className="lg:w-5 w-full"
              />
            )}
            <Fieldset legend="Skill Challenge Criteria" className="lg:w-5 w-full">
              {siteSpecificSkillInfoBySkillCode?.assessmentInfo}
            </Fieldset>

            <Fieldset legend="Essential Question" className="w-full">
              {siteSpecificSkillInfoBySkillCode?.essentialQuestion}
            </Fieldset>
            <div className="w-full flex flex-wrap p-2" id="primer">
              <div id="print-about-skill-title" style={{ display: 'none' }}>
                <h2>{siteSpecificSkillInfoBySkillCode?.name}</h2>
              </div>

              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(splitSkillContext(siteSpecificSkillInfoBySkillCode?.context || '').first),
                }}
                className="lg:w-5 lg:mr-6 w-full"
              ></div>
              <div
                className="lg:w-5 w-full"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(splitSkillContext(siteSpecificSkillInfoBySkillCode?.context || '').second),
                }}
              ></div>
            </div>
            <Accordion className="w-full flex flex-column gap-lg mb-3">
              <AccordionTab header={<h3>Standards & Curricula</h3>} className="nc_shadowed">
                <div className="flex flex-wrap justify-content-between gap-lg">
                  <Fieldset legend="Curriculum Alignment Documents" className="lg:w-5 w-full">
                    <ul style={{ listStyleType: 'none' }}>
                      <li className="pb-1">
                        Illustrative Math Lesson Alignment
                        <a
                          href={presignedURLsForSkill?.illustrativeMathAlignmentUrl || ''}
                          target="blank"
                          className="ml-2"
                        >
                          <span className="pi pi-file-pdf"></span>
                        </a>
                        <a
                          href="https://docs.google.com/spreadsheets/d/1R8xxkMiP2O67YCqi18oLKV2obZ-p4h0APWDxUom7owY/copy"
                          target="blank"
                          className="ml-2"
                        >
                          <span className="pi pi-file-excel"></span>
                        </a>
                      </li>
                      <li className="pb-1">
                        Reveal Unit Alignment
                        <a href={presignedURLsForSkill?.revealMathAlignmentUrl || ''} target="blank" className="ml-2">
                          <span className="pi pi-file-pdf"></span>
                        </a>
                      </li>
                    </ul>
                  </Fieldset>
                  {siteSpecificSkillInfoBySkillCode &&
                    siteSpecificSkillInfoBySkillCode?.standards?.map(el => {
                      return (
                        <Fieldset key={el?.id} legend={el?.longName} className="lg:w-5 w-full">
                          {el?.description}
                        </Fieldset>
                      );
                    })}
                </div>
              </AccordionTab>
              <AccordionTab header={<h3>Additional Lessons & Resources</h3>} className="nc_shadowed">
                {presignedURLsForSkill?.workedExampleVideoUrl && (
                  <div className="flex flex-column nc_content-frame m-0">
                    <Divider align="left" className="m-0">
                      <h3>Worked Example</h3>
                    </Divider>
                    <ul className="m-0">
                      <li className="text-primary">
                        <div className="w-9">
                          <span className="cursor-pointer" onClick={() => setVideoModalVisible(true)}>
                            Worked Example Video
                          </span>

                          <span className="text-color ml-1">
                            This video shows students strategies for working through a sample math problem to enhance
                            their understanding of this skill.
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>
                )}
                {!disableKhanForMiami(
                  userRedux?.customBrandingApplied,
                  lessonSkillsQuery?.lessonSkills?.nodes?.find(item => item?.lesson?.name === 'Skill Overview')?.lesson
                    ?.url,
                ) && (
                  <div className="flex flex-column nc_content-frame m-0">
                    <Divider align="left" className="m-0">
                      <h3>Digital Lessons</h3>
                    </Divider>
                    <ul className="m-0">
                      <>
                        <li className="mb-3 text-primary">
                          <div className="w-9">
                            <span
                              className="cursor-pointer"
                              onClick={() =>
                                window.open(
                                  lessonSkillsQuery?.lessonSkills?.nodes?.find(
                                    item => item?.lesson?.name === 'Skill Overview',
                                  )?.lesson?.url || '',
                                )
                              }
                            >
                              English
                            </span>

                            <span className="text-color ml-1">
                              {
                                lessonSkillsQuery?.lessonSkills?.nodes?.find(
                                  item => item?.lesson?.name === 'Skill Overview',
                                )?.lesson?.summary
                              }
                            </span>
                          </div>
                          <div className="flex justify-content-end mb-2">
                            <span>{tag()}</span>
                          </div>
                        </li>
                        {lessonSkillsQuery?.lessonSkills?.nodes?.find(item => item?.lesson?.name === 'Skill Overview')
                          ?.lesson?.spanishUrl && (
                          <li className="mb-3 text-primary">
                            <div className="w-9">
                              <span
                                className="cursor-pointer"
                                onClick={() =>
                                  window.open(
                                    lessonSkillsQuery?.lessonSkills?.nodes?.find(
                                      item => item?.lesson?.name === 'Skill Overview',
                                    )?.lesson?.spanishUrl || '',
                                  )
                                }
                              >
                                Spanish
                              </span>

                              <span className="text-color ml-1">
                                {
                                  lessonSkillsQuery?.lessonSkills?.nodes?.find(
                                    item => item?.lesson?.name === 'Skill Overview',
                                  )?.lesson?.summary
                                }
                              </span>
                            </div>
                            <div className="flex justify-content-end mb-2">
                              <span>{tag()}</span>
                            </div>
                          </li>
                        )}
                      </>
                    </ul>
                  </div>
                )}
                {getLessonsInfo(lessonSkillsQuery)
                  .filter(item => !item.lessons.some(lesson => lesson.teacherLed))
                  .map((item, key) => {
                    if (item.lessons.length) {
                      return (
                        <div key={key} className="flex flex-column nc_content-frame m-0">
                          <Divider align="left" className="m-0">
                            <h3>{item.title}</h3>
                          </Divider>
                          <ul>
                            {item.lessons.map(lesson => {
                              if (!disableKhanForMiami(userRedux?.customBrandingApplied, lesson.link))
                                return (
                                  <li key={lesson.id} className="text-primary">
                                    <div className="w-9">
                                      <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (lesson.internal) {
                                            setSelectedLesson({
                                              id: lesson.id || '',
                                              url: lesson.link || '',
                                              name: lesson.name || '',
                                              summary: lesson.description || '',
                                            });
                                            setModalVisible(true);
                                          } else {
                                            window.open(lesson.link || '');
                                          }
                                        }}
                                      >
                                        {lesson.name}
                                      </span>
                                      <span className="text-color ml-1">{lesson.description}</span>
                                    </div>

                                    {!lesson.teacherOnly && (
                                      <div className="flex justify-content-end mb-2">
                                        <span>{tag()}</span>
                                      </div>
                                    )}
                                  </li>
                                );
                            })}
                          </ul>
                        </div>
                      );
                    }
                  })}
              </AccordionTab>
              <AccordionTab header={vocabHeader} className="nc_shadowed">
                <div className="flex flex-wrap justify-content-between gap-lg">
                  {siteSpecificSkillInfoBySkillCode?.vocabularies?.map(element => (
                    <Fieldset key={element?.id} legend={element?.term} className="lg:w-5 w-full">
                      {element?.definition}
                    </Fieldset>
                  ))}
                </div>
              </AccordionTab>
              <AccordionTab header={whenWillIUseThisHeader} className="nc_shadowed">
                <div className="flex flex-wrap justify-content-between gap-lg">
                  {siteSpecificSkillInfoBySkillCode?.skillStories?.map(element => (
                    <Fieldset key={element?.id} legend={element?.profession} className="lg:w-5 w-full">
                      {element?.description}
                    </Fieldset>
                  ))}
                </div>
              </AccordionTab>
            </Accordion>
            <Fieldset
              legend="Resource Center"
              onClick={() => window.open('https://teachtoone.org/resource-center')}
              className="lg:w-5 w-full nc_fieldset-link"
            >
              This repository is overflowing with information that will help you succeed in implementing{' '}
              <em>Teach to One Roadmaps</em>! This is an organized, accessible, and helpful website with the most
              updated resources to help your daily instruction. From ready-to-print handouts to signing up for upcoming
              webinars, this knowledge base supports your understanding of the product.
            </Fieldset>
          </div>
        </TabPanel>
        <TabPanel className="gap-lg" header="Practice Problems">
          {
            <Toolbar
              end={
                <Button
                  loading={fetchingPdf}
                  disabled={printDisabled}
                  onClick={() =>
                    downloadPdf(setFetchingPdf, presignedURLsForSkill?.practiceProblemsUrl || '', skillCode)
                  }
                  label="Print Worksheet"
                  icon="nc_icon-nc_print icon-sm"
                  outlined
                  rounded
                  aria-label="Print Worksheets"
                ></Button>
              }
              className="w-full nc_block-header"
            />
          }
          <PracticeProblems
            setPrintDisabled={setPrintDisabled}
            skillCode={skillCode || ''}
            skillId=""
            skipSessionQuery={true}
          />
        </TabPanel>
      </TabView>
      {selectedLesson && (
        <AlignedContentModal visible={modalVisible} setVisible={setModalVisible} lesson={selectedLesson} />
      )}
      <Dialog visible={videoModalVisible} onHide={() => setVideoModalVisible(false)}>
        <WorkedExample
          videoUrl={presignedURLsForSkill?.workedExampleVideoUrl}
          captionUrlEnglish={presignedURLsForSkill?.workedExampleVideoEnglishCaptionUrl}
          captionUrlSpanish={presignedURLsForSkill?.workedExampleVideoSpanishCaptionUrl}
        />
      </Dialog>
    </div>
  );
};
